/**
 * Admin Gallery Tab - Manage photo gallery
 */
import { useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Cat, Star, Trash2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useAdmin } from "./AdminContext";
import { API } from "../../lib/api";

const AdminGalleryTab = () => {
  const { galleryImages, setGalleryImages, loadData } = useAdmin();
  
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageForm, setImageForm] = useState({ cat_name: "", caption: "", featured: false });
  const [selectedFile, setSelectedFile] = useState(null);

  const handleImageUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) return;
    
    setUploadingImage(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result.split(",")[1];
        await axios.post(`${API}/gallery`, {
          ...imageForm,
          image_base64: base64,
          image_mime_type: selectedFile.type
        }, { withCredentials: true });
        toast.success("Photo uploaded!");
        setImageForm({ cat_name: "", caption: "", featured: false });
        setSelectedFile(null);
        loadData();
      };
      reader.readAsDataURL(selectedFile);
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleToggleFeatured = async (img) => {
    try {
      await axios.put(`${API}/gallery/${img.image_id}`, { featured: !img.featured }, { withCredentials: true });
      setGalleryImages(galleryImages.map(i => i.image_id === img.image_id ? {...i, featured: !i.featured} : i));
    } catch { toast.error("Failed to update"); }
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await axios.delete(`${API}/gallery/${imageId}`, { withCredentials: true });
      setGalleryImages(galleryImages.filter(i => i.image_id !== imageId));
      toast.success("Photo deleted");
    } catch { toast.error("Delete failed"); }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Photo Gallery</CardTitle>
        <CardDescription>Upload and manage photos of cats</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleImageUpload} className="mb-8 p-4 bg-amber-50 rounded-lg border border-amber-200">
          <h3 className="font-semibold text-stone-900 mb-4">Upload New Photo</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>Image File *</Label>
              <Input type="file" accept="image/*" onChange={e => setSelectedFile(e.target.files?.[0])} />
            </div>
            <div>
              <Label>Cat Name</Label>
              <Input value={imageForm.cat_name} onChange={e => setImageForm({...imageForm, cat_name: e.target.value})} placeholder="e.g., Whiskers" />
            </div>
            <div>
              <Label>Caption</Label>
              <Input value={imageForm.caption} onChange={e => setImageForm({...imageForm, caption: e.target.value})} placeholder="e.g., Enjoying sunshine" />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="featured" checked={imageForm.featured} onChange={e => setImageForm({...imageForm, featured: e.target.checked})} />
              <label htmlFor="featured">Feature this photo</label>
            </div>
          </div>
          <Button type="submit" disabled={uploadingImage || !selectedFile} className="mt-4 bg-amber-600 hover:bg-amber-700">
            {uploadingImage ? "Uploading..." : "Upload Photo"}
          </Button>
        </form>
        
        {galleryImages.length === 0 ? (
          <p className="text-center text-stone-500 py-8">No photos yet</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {galleryImages.map(img => (
              <div key={img.image_id} className="relative group rounded-lg overflow-hidden">
                {img.image_base64 ? (
                  <img src={`data:${img.image_mime_type || 'image/jpeg'};base64,${img.image_base64}`} alt={img.cat_name} className="w-full aspect-square object-cover" />
                ) : (
                  <div className="w-full aspect-square bg-gradient-to-br from-amber-100 to-stone-200 flex flex-col items-center justify-center p-2">
                    <Cat className="w-10 h-10 text-amber-500 mb-2" />
                    <p className="text-xs text-stone-600 text-center font-medium">{img.cat_name || "Photo"}</p>
                    <p className="text-xs text-stone-500 text-center">{img.caption || img.filename || ""}</p>
                  </div>
                )}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-2">
                  <p className="text-white text-sm font-medium text-center">{img.cat_name || "Unnamed"}</p>
                  {img.caption && <p className="text-white/80 text-xs text-center">{img.caption}</p>}
                  <div className="flex gap-2 mt-2">
                    <Button size="sm" variant={img.featured ? "default" : "outline"} onClick={() => handleToggleFeatured(img)} className={img.featured ? "bg-amber-500" : "bg-white/20 text-white"}>
                      <Star className={`w-4 h-4 ${img.featured ? 'fill-white' : ''}`} />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleDeleteImage(img.image_id)} className="bg-red-500/80 text-white">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                {img.featured && <Star className="absolute top-2 right-2 w-5 h-5 text-amber-400 fill-amber-400" />}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminGalleryTab;
